import {FormField} from "../form-field/form-field";

const DEFAULT_PREFIX = "+34";
const DEFAULT_ITEM = [DEFAULT_PREFIX,"es","España "+DEFAULT_PREFIX];

/**
 * Builds the flag image URL for a given ISO-3166 alpha-2 code (e.g. "es").
 * Reuses the same SVG set served for the control panel's telephone-prefix widget.
 * @param {string} iso2
 * @returns {string}
 */
function flagIconUrl(iso2){
    return "/icons/flags/"+(iso2||"es")+".svg";
}

export class FormFieldTelephonePrefix extends FormField{
    constructor(scope,$root){
        super(scope,$root);
        this.items          = [];
        this.currentPrefix  = DEFAULT_PREFIX;
        this.$prefixBtn     = null;
        this.$prefixFlag    = null;
        this.$prefixCode    = null;
        this.$prefixMenu    = null;
        this.$prefixSearch  = null;
        this.$prefixOptions = null;
        this.$number        = null;
        this.$value         = null;
    }

    /**
     * @param {string} prefix
     * @returns {[string,string,string]}
     */
    _findItem(prefix){
        return this.items.find((item) => item[0]===prefix) || this.items[0] || DEFAULT_ITEM;
    }

    /**
     * Finds the longest matching dial-code prefix at the start of value
     * @param {string} value
     * @returns {string|null}
     */
    _extractPrefix(value){
        if(!value){
            return null;
        }
        const sorted = this.items.slice().sort((a,b) => b[0].length - a[0].length);
        for(const item of sorted){
            if(value.indexOf(item[0])===0){
                return item[0];
            }
        }
        return null;
    }

    _applyPrefix(prefix){
        this.currentPrefix = prefix;
        const item = this._findItem(prefix);
        this.$prefixCode.text(item[0]);
        this.$prefixFlag.attr("src",flagIconUrl(item[1]));
    }

    _renderOptions(query){
        const q = (query||"").toLowerCase().trim();
        // this.scope is a CSS selector (leading dot, e.g. ".js-form-field-telephone-prefix"),
        // used by findMember(); a class *attribute* value must not include that dot.
        const scopeClass = this.scope.replace(/^\./,"");
        this.$prefixOptions.empty();
        this.items
            .filter((item) => !q || item[2].toLowerCase().indexOf(q)!==-1 || item[0].indexOf(q)!==-1)
            .forEach((item) => {
                $("<button type='button' class='"+scopeClass+"__prefix-option'></button>")
                    .append($("<img alt='' width='19' height='14'>").addClass(scopeClass+"__prefix-option-flag").attr("src",flagIconUrl(item[1])))
                    .append($("<span></span>").addClass(scopeClass+"__prefix-option-label").text(item[2]))
                    .on("click",() => {
                        this._applyPrefix(item[0]);
                        this._sync();
                        this._closeMenu();
                        this.$root.trigger("formField:changeVal");
                    })
                    .appendTo(this.$prefixOptions);
            });
    }

    /**
     * Positions the (body-attached) menu right below the toggle button,
     * so it escapes any `overflow:hidden` ancestor (e.g. the dynamic form card).
     */
    _positionMenu(){
        const rect = this.$prefixBtn.get(0).getBoundingClientRect();
        this.$prefixMenu.css({
            top: Math.round(rect.bottom + 4) + "px",
            left: Math.round(rect.left) + "px"
        });
    }

    _openMenu(){
        this.$root.addClass("is-menu-open");
        this._positionMenu();
        this.$prefixMenu.addClass("is-open");
        this.$prefixSearch.val("");
        this._renderOptions("");
        this.$prefixSearch.focus();

        // Closes on any scroll outside the menu itself (window or a nested scrollable
        // ancestor, e.g. the form card) rather than trying to keep a fixed-position
        // menu perfectly aligned. Scrolling the options list must not close it.
        this._closeOnResize = () => this._closeMenu();
        this._closeOnScroll = (ev) => {
            if(this.$prefixMenu.get(0).contains(ev.target)){
                return;
            }
            this._closeMenu();
        };
        window.addEventListener("resize",this._closeOnResize);
        document.addEventListener("scroll",this._closeOnScroll,true);
    }

    _closeMenu(){
        this.$root.removeClass("is-menu-open");
        this.$prefixMenu.removeClass("is-open");
        if(this._closeOnResize){
            window.removeEventListener("resize",this._closeOnResize);
            document.removeEventListener("scroll",this._closeOnScroll,true);
            this._closeOnResize = null;
            this._closeOnScroll = null;
        }
    }

    _sync(){
        const number = (this.$number.val()||"").replace(/[^\d]/g,"");
        this.$value.val(number?this.currentPrefix+number:"");
    }

    getVal(){
        return this.$value.val() || null;
    }

    setVal(value,noEvent){
        const complete = typeof value==="string"?value:"";
        const matchedPrefix = this._extractPrefix(complete);
        this._applyPrefix(matchedPrefix || DEFAULT_PREFIX);
        this.$number.val(matchedPrefix ? complete.slice(matchedPrefix.length) : complete);
        this._sync();
        if(!noEvent){
            this.$root.trigger("formField:changeVal");
        }
    }

    ready(){
        super.ready();

        this.items = this.$root.data("prefix-items") || [DEFAULT_ITEM];

        this.$prefixBtn     = this.findMember("prefix-btn");
        this.$prefixFlag    = this.findMember("prefix-flag").attr("width",19).attr("height",14);
        this.$prefixCode    = this.findMember("prefix-code");
        this.$prefixMenu    = this.findMember("prefix-menu");
        this.$prefixSearch  = this.findMember("prefix-search");
        this.$prefixOptions = this.findMember("prefix-options");
        this.$number        = this.findMember("number");
        this.$value         = this.findMember("value");

        // Detach the menu to <body> so it escapes any `overflow:hidden` ancestor
        // (e.g. the dynamic form card); positioned via _positionMenu() on open.
        this.$prefixMenu.appendTo(document.body);

        this.$prefixBtn.on("click",(ev) => {
            ev.preventDefault();
            if(this.$root.hasClass("is-menu-open")){
                this._closeMenu();
            }else{
                this._openMenu();
            }
        });

        this.$prefixSearch.on("input",() => {
            this._renderOptions(this.$prefixSearch.val());
        });

        this.$prefixSearch.on("click",(ev) => ev.stopPropagation());

        $(document).on("click",(ev) => {
            if(!this.$root.hasClass("is-menu-open")){
                return;
            }
            const $target = $(ev.target);
            // The menu lives in <body>, not under $root, so it must be checked separately.
            if(!$target.closest(this.$root).length && !$target.closest(this.$prefixMenu).length){
                this._closeMenu();
            }
        });

        this.$number.on("input change",() => {
            this._sync();
            this.$root.trigger("formField:changeVal");
        });

        this.$number.blur((ev) => {
            this.$root.trigger("formField:checkRestrictions",[{"input": $(ev.currentTarget)}]);
        });

        this.setVal(this.$value.val(),true);
    }
}
